import download from 'downloadjs';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { PhonesPro } from '@servicetitan/application-data/telecom';
import { UserStore } from '@servicetitan/application-data/user';
import { NotificationsService } from '@servicetitan/notifications';
import { inject, injectable } from '@servicetitan/react-ioc';

@injectable()
export class PhoneNumbersExportStore {
    @observable user?: string;
    @observable timestamp?: Date;
    @observable resultId?: string;

    constructor(
        @inject(PhonesPro) private phonesProApi: PhonesPro,
        @inject(NotificationsService) private notifications: NotificationsService,
        @inject(UserStore) private userStore: UserStore
    ) {
        makeObservable(this);
    }

    loadData = async () => {
        const response = await this.phonesProApi.getLastPhoneNumbersExport();
        if (!response || !response.data) {
            return;
        }

        runInAction(() => {
            const data = response!.data!;
            this.user = this.getUserName(data.userId);
            this.timestamp = data.timestamp;
            this.resultId = data.resultId;
        });
    };

    bindEventHandlers = () => {
        window.pusherInstance.bind('DataExportEvent', this.handlePhoneNumbersExportEvent);
    };

    unbindEventHandlers = () => {
        window.pusherInstance.global_emitter.unbind(
            'DataExportEvent',
            this.handlePhoneNumbersExportEvent
        );
    };

    export = async () => {
        const response = await this.phonesProApi.exportPhoneNumbers();
        if (!response || !response.data) {
            return;
        }

        runInAction(() => {
            const data = response!.data!;
            if (data.success) {
                this.notifications.success({
                    title: 'Export successfully started',
                });
                this.resultId = data.result;
            } else {
                this.notifications.error({
                    title: 'Error while exporting data',
                    message: data.message,
                });
            }
        });
    };

    download = async () => {
        this.phonesProApi.phoneNumbersExportResult(this.resultId!).then(file => {
            download(file.data, `PhoneNumbers_${this.timestamp}.xlsx`);
        });
    };

    handlePhoneNumbersExportEvent = (data: {
        Type: string;
        ResultId: string;
        Success: boolean;
        Message: string;
        Timestamp: string;
        UserId: number;
    }) => {
        if (data.ResultId === this.resultId) {
            if (data.Success) {
                this.updateLastExport(data);

                if (data.UserId === this.userStore.userId) {
                    this.notifications.success({
                        title: 'Export successfully completed',
                    });

                    this.download();
                }
            } else {
                this.notifications.error({
                    title: 'Error while exporting data',
                    message: data.Message,
                });
            }
        } else if (data.Type === 'PhoneNumbers' && data.Success) {
            this.updateLastExport(data);
        }
    };

    @action updateLastExport = async (data: {
        Timestamp: string;
        UserId: number;
        ResultId: string;
    }) => {
        const date = new Date(data.Timestamp);
        if (!this.timestamp || this.timestamp < date) {
            this.timestamp = date;
            this.user = this.getUserName(data.UserId);
            this.resultId = data.ResultId;
        }
    };

    getUserName = (userId: number) =>
        window.App.Data.Users.find((x: { Id: number }) => x.Id === userId)?.Name;
}
